/**
 * The From addresses a campaign may send as, and the Reply-To addresses replies
 * may be routed to. Shared by the app (to populate the dropdowns) and the
 * functions (to validate the choices server-side), so the two can never
 * disagree about what is allowed.
 *
 * From and Reply-To are independent:
 *  - Every From address MUST be on the Postmark-verified sending domain — an
 *    unaligned From fails DMARC.
 *  - Reply-To does not affect DMARC, so it can be any staff address. The
 *    signed-in user is always offered (and is the default) in addition to the
 *    fixed options below.
 */
export interface Sender {
  /** The From mailbox. Must be on the verified sending domain. */
  email: string;
  /** Display name shown to recipients, e.g. "Rough Creek Lodge". */
  name: string;
}

export const ALLOWED_SENDERS: Sender[] = [
  { email: 'email@sales.roughcreek.com', name: 'Rough Creek Lodge' },
  { email: 'sales@sales.roughcreek.com', name: 'Rough Creek Lodge — Sales' },
];

/** The From used when none is chosen, or when a stored choice is no longer allowed. */
export const DEFAULT_SENDER: Sender = ALLOWED_SENDERS[0];

/**
 * Fixed Reply-To choices offered in addition to the signed-in user. Replies to
 * a campaign land in whichever address is selected.
 */
export const REPLY_TO_OPTIONS: string[] = [
  'larry@roughcreek.com',
  'jenn@roughcreek.com',
  'kkernick@roughcreek.com',
];

/** Case-insensitive lookup of an allowed sender by address. */
export function findAllowedSender(email: string | undefined): Sender | undefined {
  const needle = (email ?? '').trim().toLowerCase();
  return ALLOWED_SENDERS.find((s) => s.email.toLowerCase() === needle);
}

/** Loose but effective single-address check — no display names, no lists. */
export function isValidEmail(email: string | undefined): boolean {
  return !!email && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
}
